/* eslint-disable */
import { TextField } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import DeleteIcon from "@material-ui/icons/Delete";
import CardTitle from "@saleor/components/CardTitle";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import StatusLabel from "@saleor/components/StatusLabel";
import TableCellAvatar, {
  AVATAR_MARGIN
} from "@saleor/components/TableCellAvatar";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { FormChange } from "@saleor/hooks/useForm";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { maybe, renderCollection } from "../../../misc";
import { ListActions, ListProps, ReorderAction } from "../../../types";
import { SaleDetails_sale } from "../../types/SaleDetails";
import { SaleDetailsPageFormData } from "../SaleDetailsPage";
import { SaleType } from "@saleor/types/globalTypes";
import {
  SortableTableBody,
  SortableTableRow
} from "@saleor/components/SortableTable";

export interface DiscountSaleProducts extends ListProps, ListActions {
  discount: SaleDetails_sale;
  onProductAssign: () => void;
  onProductUnassign: (id: string) => void;
  onChange: FormChange;
  data: SaleDetailsPageFormData;
  onProductReorder: ReorderAction;
}

const useStyles = makeStyles(
  theme => ({
    colActions: {
      "&:last-child": {
        paddingRight: 0
      },
      width: 76 + theme.spacing(0.5)
    },
    colName: {
      paddingLeft: 0,
      width: 200
    },
    colNameLabel: {
      marginLeft: AVATAR_MARGIN + theme.spacing(3)
    },
    colPublished: {
      width: 150
    },
    colType: {
      width: 130,
      padding: 0
    },
    colGrab: {
      width: 60
    },
    colInventory: {
      width: 200
    },
    colPrice: {
      // width: 200
    },
    table: {
      tableLayout: "fixed"
    },
    tableRow: {
      cursor: "pointer"
    }
  }),
  { name: "DiscountSaleProducts" }
);

// const numberOfColumns = 8;

const DiscountSaleProducts: React.FC<DiscountSaleProducts> = props => {
  const {
    discount: sale,

    disabled,
    pageInfo,
    onRowClick,
    onPreviousPage,
    onProductAssign,
    onProductUnassign,
    onNextPage,
    isChecked,
    selected,
    toggle,
    toggleAll,
    toolbar,
    onChange,
    data,
    onProductReorder
  } = props;
  const classes = useStyles(props);
  const numberOfColumns = SaleType.CUSTOM === data.type ? 8 : 5;

  const intl = useIntl();
  const hasSaleProducts = maybe(() =>
    sale.saleProducts.edges.map(edge => edge.node)
  )?.length;

  return (
    <Card>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Eligible Products",
          description: "section header"
        })}
        toolbar={
          <Button color="primary" onClick={onProductAssign}>
            <FormattedMessage
              defaultMessage="Assign products"
              description="button"
            />
          </Button>
        }
      />
      <ResponsiveTable>
        {SaleType.CUSTOM !== data.type ? (
          <colgroup>
            {hasSaleProducts ? <col /> : null}
            <col className={classes.colName} />
            <col className={classes.colType} />
            <col className={classes.colPublished} />
            <col className={classes.colActions} />
          </colgroup>
        ) : (
          <colgroup>
            <col className={classes.colGrab} />
            <col />
            <col className={classes.colName} />
            <col className={classes.colType} />
            <col className={classes.colPrice} />
            <col className={classes.colPublished} />
            <col className={classes.colActions} />
          </colgroup>
        )}
        <TableHead
          colSpan={numberOfColumns}
          selected={selected}
          disabled={disabled}
          items={maybe(() =>
            SaleType.CUSTOM === data.type
              ? sale.customProducts.map(item => item.product)
              : sale.saleProducts.edges.map(edge => edge.node.product)
          )}
          toggleAll={toggleAll}
          toolbar={toolbar}
          dragRows={SaleType.CUSTOM === data.type}
        >
          <TableCell className={classes.colName}>
            <span className={hasSaleProducts ? classes.colNameLabel : ""}>
              <FormattedMessage defaultMessage="Product Name" />
            </span>
          </TableCell>
          <TableCell className={classes.colType}>
            <FormattedMessage defaultMessage="Product Type" />
          </TableCell>
          {SaleType.CUSTOM === data.type ? (
            <>
              <TableCell className={classes.colInventory}>
                <FormattedMessage defaultMessage="Inventory" />
              </TableCell>
              <TableCell className={classes.colPrice}>
                <FormattedMessage defaultMessage="Discount Price" />
              </TableCell>
            </>
          ) : null}
          <TableCell className={classes.colPublished}>
            <FormattedMessage
              defaultMessage="Published"
              description="product is published"
            />
          </TableCell>
          <TableCell className={classes.colActions} />
        </TableHead>
        {SaleType.CUSTOM === data.type ? (
          <TableFooter></TableFooter>
        ) : (
          <TableFooter>
            <TableRow>
              <TablePagination
                colSpan={numberOfColumns}
                hasNextPage={
                  pageInfo && !disabled ? pageInfo.hasNextPage : false
                }
                onNextPage={onNextPage}
                hasPreviousPage={
                  pageInfo && !disabled ? pageInfo.hasPreviousPage : false
                }
                onPreviousPage={onPreviousPage}
              />
            </TableRow>
          </TableFooter>
        )}
        {SaleType.CUSTOM === data.type ? (
          <SortableTableBody onSortEnd={onProductReorder}>
            {renderCollection(
              // maybe(() => sale.products.edges.map(edge => edge.node)),
              maybe(() => sale?.customProducts),
              (product, index) => {
                if (!product) {
                  return undefined;
                }
                const isSelected = product
                  ? isChecked(product.product.id)
                  : false;
                return (
                  <SortableTableRow
                    className={classes.tableRow}
                    index={index || 0}
                    hover={!!product}
                    key={product ? product.product.id : "skeleton"}
                    selected={isSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        disabled={disabled}
                        disableClickPropagation
                        onChange={() => toggle(product.product.id)}
                      />
                    </TableCell>
                    <TableCellAvatar
                      className={classes.colName}
                      thumbnail={maybe(() => product.product.thumbnail.url)}
                      onClick={product && onRowClick(product.product.id)}
                    >
                      {maybe<React.ReactNode>(
                        () => product.product.name,
                        <Skeleton />
                      )}
                    </TableCellAvatar>
                    <TableCell className={classes.colType}>
                      {maybe<React.ReactNode>(
                        () => product.product.productType.name,
                        <Skeleton />
                      )}
                    </TableCell>

                    <TableCell className={classes.colInventory}>
                      <TextField
                        disabled={disabled}
                        // fullWidth
                        label={intl.formatMessage({
                          defaultMessage: "Sales Inventory",
                          description: "sale discount"
                        })}
                        onClick={event => event.stopPropagation()}
                        // name={"usageLimit" as keyof FormData}
                        name={
                          ("products." +
                            index +
                            ".usageLimit") as keyof SaleDetailsPageFormData
                        }
                        // helperText={getDiscountErrorMessage(formErrors.value, intl)}
                        value={data.products[index]?.usageLimit}
                        onChange={e => {
                          onChange(e);
                        }}
                      />
                    </TableCell>
                    <TableCell className={classes.colPrice}>
                      <TextField
                        disabled={disabled}
                        // fullWidth
                        label={intl.formatMessage({
                          defaultMessage: "Discount Price",
                          description: "sale discount"
                        })}
                        onClick={event => event.stopPropagation()}
                        // name={"discountPrice" as keyof FormData}
                        name={
                          ("products." +
                            index +
                            ".discountPrice") as keyof SaleDetailsPageFormData
                        }
                        // index={index}
                        // helperText={getDiscountErrorMessage(formErrors.value, intl)}
                        value={data.products[index]?.discountPrice}
                        onChange={e => {
                          // const event = Object.assign(
                          //   {},
                          //   e,
                          //   Object.assign(e.target, { index: index })
                          // );
                          // // e.target.index = index
                          // onChange(event);
                          onChange(e);
                        }}
                      />
                    </TableCell>
                    <TableCell className={classes.colPublished}>
                      {product && product.product.isPublished !== undefined ? (
                        <StatusLabel
                          label={
                            product.product.isPublished
                              ? intl.formatMessage({
                                  defaultMessage: "Published",
                                  description: "product is published"
                                })
                              : intl.formatMessage({
                                  defaultMessage: "Not published",
                                  description: "product is not published"
                                })
                          }
                          status={
                            product.product.isPublished ? "success" : "error"
                          }
                        />
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colActions}>
                      <IconButton
                        disabled={!product || disabled}
                        onClick={event => {
                          event.stopPropagation();
                          onProductUnassign(product.product.id);
                        }}
                      >
                        <DeleteIcon color="primary" />
                      </IconButton>
                    </TableCell>
                  </SortableTableRow>
                );
              },
              () => (
                <TableRow>
                  <TableCell colSpan={numberOfColumns}>
                    <FormattedMessage defaultMessage="No products found" />
                  </TableCell>
                </TableRow>
              )
            )}
          </SortableTableBody>
        ) : (
          <TableBody>
            {renderCollection(
              // maybe(() => sale.products.edges.map(edge => edge.node)),
              maybe(() => sale?.saleProducts?.edges?.map(edge => edge.node)),
              product => {
                if (!product) {
                  return undefined;
                }
                const isSelected = product
                  ? isChecked(product.product.id)
                  : false;
                return (
                  <TableRow
                    hover={!!product}
                    key={product ? product.id : "skeleton"}
                    className={classes.tableRow}
                    selected={isSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        disabled={disabled}
                        disableClickPropagation
                        onChange={() => toggle(product.product.id)}
                      />
                    </TableCell>
                    <TableCellAvatar
                      className={classes.colName}
                      thumbnail={maybe(() => product.product.thumbnail.url)}
                      onClick={product && onRowClick(product.product.id)}
                    >
                      {maybe<React.ReactNode>(
                        () => product.product.name,
                        <Skeleton />
                      )}
                    </TableCellAvatar>
                    <TableCell className={classes.colType}>
                      {maybe<React.ReactNode>(
                        () => product.product.productType.name,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colPublished}>
                      {product && product.product.isPublished !== undefined ? (
                        <StatusLabel
                          label={
                            product.product.isPublished
                              ? intl.formatMessage({
                                  defaultMessage: "Published",
                                  description: "product is published"
                                })
                              : intl.formatMessage({
                                  defaultMessage: "Not published",
                                  description: "product is not published"
                                })
                          }
                          status={
                            product.product.isPublished ? "success" : "error"
                          }
                        />
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colActions}>
                      <IconButton
                        disabled={!product || disabled}
                        onClick={event => {
                          event.stopPropagation();
                          onProductUnassign(product.product.id);
                        }}
                      >
                        <DeleteIcon color="primary" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              },
              () => (
                <TableRow>
                  <TableCell colSpan={numberOfColumns}>
                    <FormattedMessage defaultMessage="No products found" />
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        )}
      </ResponsiveTable>
    </Card>
  );
};
DiscountSaleProducts.displayName = "DiscountSaleProducts";
export default DiscountSaleProducts;
